"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  INPUT_MONO,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { escapeHtml } from "@/lib/tool-kit/citation-parts";
import {
  BUDGET_PRESETS,
  type BudgetPreset,
  type BudgetRow,
  budgetTotals,
  buildBudget,
  DEFAULT_PARAGRAPH_WORDS,
  DEFAULT_SENTENCE_WORDS,
  LONG_PARAGRAPH_WORDS,
  PRESET_SECTIONS,
  PRESET_SHAPES,
  pagesToWords,
  scaleTo100,
  WORDS_PER_PAGE,
} from "@/lib/tool-kit/essay/word-budget";

/** English strings. A translated content file overrides these through `ui`. */
export const ESSAY_WORD_BUDGET_CALCULATOR_STRINGS = {
  modeLabel: "Is your limit in words or pages?",
  modes: { words: "Words", pages: "Pages" },
  wordsLabel: "Word limit",
  wordsPlaceholder: "e.g. 2000",
  pagesLabel: "Page limit",
  pagesPlaceholder: "e.g. 8",
  spacingLabel: "Words per page",
  spacings: {
    double: "250 — double-spaced",
    oneAndHalf: "333 — 1.5 line spacing",
    single: "500 — single-spaced",
    custom: "Custom",
  },
  spacingHint:
    "Assumes 12-point Times New Roman with 1-inch margins on US Letter paper. Other fonts and layouts change the count.",
  customLabel: "Your words per page",
  presetLabel: "Structure",
  presets: {
    fiveParagraph: "Five-paragraph essay",
    argumentative: "Argumentative essay",
    compareContrast: "Compare-and-contrast essay",
    imrad: "Research paper (IMRaD)",
    literatureReview: "Literature review",
    dissertation: "Dissertation or thesis (chapters)",
  },
  sections: PRESET_SECTIONS,
  paragraphLabel: "Target paragraph length (words)",
  sentenceLabel: "Average sentence length (words)",
  tryExample: "Try an example",
  reset: "Reset to preset",
  scale: "Scale to 100%",
  needLimit: "Enter a word or page limit to see the plan.",
  resultsHeading: "Your word budget",
  head: [
    "Section",
    "% of limit",
    "Words",
    "Paragraphs",
    "Words per paragraph",
    "≈ Sentences per paragraph",
  ],
  percentFor: "Percentage for {section}",
  totalRow: "Total",
  longMark: "!",
  longNote:
    "Rows marked ! have paragraphs over about {limit} words — consider splitting them.",
  percentOff:
    "Your sections add up to {percent}% of the limit, so the plan covers {planned} of {total} words.",
  wordsPlanned: "Words planned",
  paragraphsTotal: "Paragraphs",
  pagesTotal: "Pages, double-spaced",
  assumptions:
    "Paragraph counts assume about {paragraph} words per paragraph and sentence counts assume about {sentence} words per sentence — change either above. Five-paragraph essays always use one paragraph per section.",
  copyTable: "Copy table",
  copied: "Copied!",
  exampleWords: "2000",
} as const;

type Strings = typeof ESSAY_WORD_BUDGET_CALCULATOR_STRINGS;
type Mode = "words" | "pages";
type Spacing = "double" | "oneAndHalf" | "single" | "custom";

const SPACINGS: readonly Spacing[] = [
  "double",
  "oneAndHalf",
  "single",
  "custom",
];

/** An id, not prose — kept out of the strings so a translation cannot change it. */
const EXAMPLE_PRESET: BudgetPreset = "argumentative";

function wordsPerPage(spacing: Spacing, customRaw: string): number {
  if (spacing === "custom") {
    return parseNumberInput(customRaw) ?? 0;
  }
  return WORDS_PER_PAGE[spacing];
}

function presetPercents(preset: BudgetPreset, fmt: (n: number) => string) {
  return PRESET_SHAPES[preset].percents.map((value) => fmt(value));
}

function tableText(
  names: readonly string[],
  rows: readonly BudgetRow[],
  strings: Strings,
  fmt: (n: number) => string
): { text: string; html: string } {
  const cells = rows.map((row, index) => [
    names[index] ?? "",
    fmt(row.percent),
    fmt(row.words),
    fmt(row.paragraphs),
    fmt(row.wordsPerParagraph),
    fmt(row.sentencesPerParagraph),
  ]);
  const all = [[...strings.head], ...cells];
  const text = all.map((line) => line.join("\t")).join("\n");
  const html = `<table>${all
    .map(
      (line, index) =>
        `<tr>${line
          .map((cell) =>
            index === 0
              ? `<th>${escapeHtml(cell)}</th>`
              : `<td>${escapeHtml(cell)}</td>`
          )
          .join("")}</tr>`
    )
    .join("")}</table>`;
  return { text, html };
}

function BudgetTable({
  names,
  rows,
  raws,
  onChange,
  strings,
}: {
  names: readonly string[];
  rows: readonly BudgetRow[];
  raws: readonly string[];
  onChange: (index: number, value: string) => void;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const totals = budgetTotals(rows);
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {strings.head.map((cell) => (
              <th className={TH} key={cell} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // Rows follow the preset's sections in order.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
            <tr key={index}>
              <td className={TD}>{names[index]}</td>
              <td className={TD}>
                <label className="sr-only" htmlFor={`ewb-pct-${index}`}>
                  {t(strings.percentFor, { section: names[index] ?? "" })}
                </label>
                <input
                  className={`${INPUT_MONO} w-20`}
                  id={`ewb-pct-${index}`}
                  inputMode="decimal"
                  onChange={(event) => onChange(index, event.target.value)}
                  type="text"
                  value={raws[index] ?? ""}
                />
              </td>
              <td className={`${TD} font-medium`}>{fmt(row.words)}</td>
              <td className={TD}>{fmt(row.paragraphs)}</td>
              <td className={TD}>
                {fmt(row.wordsPerParagraph)}
                {row.long ? (
                  <span className="ms-1 font-semibold text-amber-600 dark:text-amber-400">
                    {strings.longMark}
                  </span>
                ) : null}
              </td>
              <td className={TD}>{fmt(row.sentencesPerParagraph)}</td>
            </tr>
          ))}
          <tr>
            <th className={`${TD} font-semibold`} scope="row">
              {strings.totalRow}
            </th>
            <td className={`${TD} font-semibold`}>{fmt(totals.percent)}</td>
            <td className={`${TD} font-semibold`}>{fmt(totals.words)}</td>
            <td className={`${TD} font-semibold`}>{fmt(totals.paragraphs)}</td>
            <td className={TD} />
            <td className={TD} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function EssayWordBudgetCalculatorWidget() {
  const strings = useToolUi(ESSAY_WORD_BUDGET_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("words");
  const [wordsRaw, setWordsRaw] = useState("");
  const [pagesRaw, setPagesRaw] = useState("");
  const [spacing, setSpacing] = useState<Spacing>("double");
  const [customRaw, setCustomRaw] = useState("");
  const [preset, setPreset] = useState<BudgetPreset>("fiveParagraph");
  const [raws, setRaws] = useState<string[]>(() =>
    presetPercents("fiveParagraph", fmt)
  );
  const [paragraphRaw, setParagraphRaw] = useState(
    String(DEFAULT_PARAGRAPH_WORDS)
  );
  const [sentenceRaw, setSentenceRaw] = useState(
    String(DEFAULT_SENTENCE_WORDS)
  );

  const total =
    mode === "words"
      ? Math.max(0, parseIntegerInput(wordsRaw) ?? 0)
      : pagesToWords(
          parseNumberInput(pagesRaw) ?? 0,
          wordsPerPage(spacing, customRaw)
        );
  const percents = raws.map((raw) => parseNumberInput(raw) ?? 0);
  const settings = {
    oneParagraphEach: PRESET_SHAPES[preset].oneParagraphEach,
    paragraphWords: parseNumberInput(paragraphRaw) ?? DEFAULT_PARAGRAPH_WORDS,
    sentenceWords: parseNumberInput(sentenceRaw) ?? DEFAULT_SENTENCE_WORDS,
  };
  const rows = total > 0 ? buildBudget(total, percents, settings) : [];
  const totals = budgetTotals(rows);
  const names = strings.sections[preset];
  const copy = tableText(names, rows, strings, fmt);

  function choosePreset(next: BudgetPreset) {
    setPreset(next);
    setRaws(presetPercents(next, fmt));
  }

  function loadExample() {
    setMode("words");
    setWordsRaw(strings.exampleWords);
    choosePreset(EXAMPLE_PRESET);
  }

  const presetOptions = BUDGET_PRESETS.map((value) => ({
    value,
    label: strings.presets[value],
  }));
  const spacingOptions = SPACINGS.map((value) => ({
    value,
    label: strings.spacings[value],
  }));

  return (
    <>
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.modeLabel}
          name="ewb-mode"
          onChange={setMode}
          options={[
            { value: "words" as const, label: strings.modes.words },
            { value: "pages" as const, label: strings.modes.pages },
          ]}
          value={mode}
        />
        {mode === "words" ? (
          <TextField
            id="ewb-words"
            label={strings.wordsLabel}
            numeric
            onChange={setWordsRaw}
            placeholder={strings.wordsPlaceholder}
            value={wordsRaw}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ewb-pages"
              label={strings.pagesLabel}
              numeric
              onChange={setPagesRaw}
              placeholder={strings.pagesPlaceholder}
              value={pagesRaw}
            />
            <SelectField
              hint={strings.spacingHint}
              id="ewb-spacing"
              label={strings.spacingLabel}
              onChange={setSpacing}
              options={spacingOptions}
              value={spacing}
            />
            {spacing === "custom" ? (
              <TextField
                id="ewb-custom"
                label={strings.customLabel}
                numeric
                onChange={setCustomRaw}
                value={customRaw}
              />
            ) : null}
          </div>
        )}
        <SelectField
          id="ewb-preset"
          label={strings.presetLabel}
          onChange={choosePreset}
          options={presetOptions}
          value={preset}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ewb-paragraph"
            label={strings.paragraphLabel}
            numeric
            onChange={setParagraphRaw}
            value={paragraphRaw}
          />
          <TextField
            id="ewb-sentence"
            label={strings.sentenceLabel}
            numeric
            onChange={setSentenceRaw}
            value={sentenceRaw}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setRaws(presetPercents(preset, fmt))}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </div>

      <section aria-live="polite" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          <CopyButton
            copiedLabel={strings.copied}
            disabled={rows.length === 0}
            html={copy.html}
            label={strings.copyTable}
            small
            text={copy.text}
          />
        </div>
        {rows.length === 0 ? (
          <Notice>{strings.needLimit}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.wordsPlanned}
                value={fmt(totals.words)}
              />
              <StatCard
                label={strings.paragraphsTotal}
                value={fmt(totals.paragraphs)}
              />
              <StatCard
                label={strings.pagesTotal}
                value={fmt(totals.words / WORDS_PER_PAGE.double, {
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                })}
              />
            </StatGrid>
            {Math.abs(totals.percent - 100) > 0.05 ? (
              <div className="flex flex-wrap items-center gap-3">
                <Notice>
                  {t(strings.percentOff, {
                    percent: fmt(totals.percent),
                    planned: fmt(totals.words),
                    total: fmt(total),
                  })}
                </Notice>
                <button
                  className={BUTTON_SMALL}
                  onClick={() =>
                    setRaws(scaleTo100(percents).map((value) => fmt(value)))
                  }
                  type="button"
                >
                  {strings.scale}
                </button>
              </div>
            ) : null}
            <BudgetTable
              names={names}
              onChange={(index, value) =>
                setRaws((previous) =>
                  previous.map((raw, position) =>
                    position === index ? value : raw
                  )
                )
              }
              raws={raws}
              rows={rows}
              strings={strings}
            />
            {rows.some((row) => row.long) ? (
              <p className={HINT}>
                {t(strings.longNote, { limit: fmt(LONG_PARAGRAPH_WORDS) })}
              </p>
            ) : null}
            <p className={HINT}>
              {t(strings.assumptions, {
                paragraph: fmt(settings.paragraphWords),
                sentence: fmt(settings.sentenceWords),
              })}
            </p>
          </>
        )}
      </section>
    </>
  );
}
